import { Badge, Box, Field, FieldLabel, Input, Text } from "@salestrak/ui";
import type { Omittable } from "@salestrak/ui";

// The typed confirmation is compared the way core compares a workspace name:
// exactly — case included — with only whitespace forgiven (trimmed, runs
// collapsed, Unicode-normalized). The name is what a person hesitates over; a
// three-letter auto-derived slug is typed without thinking.
function normalizeConfirmationName(value: string): string {
  return value.normalize("NFC").trim().replace(/\s+/g, " ");
}

export function confirmationMatches(typed: string, name: string): boolean {
  return normalizeConfirmationName(typed) === normalizeConfirmationName(name);
}

/**
 * THE type-to-confirm field for every destructive dialog that gates on a
 * name (workspace, workflow, team). One label grammar — "Type the <noun>
 * name to confirm" with the name as a badge — one placeholder (the name),
 * one match rule (`confirmationMatches`), and Enter fires `onSubmit` only
 * once the name matches, so the field and the footer's destructive button
 * agree on the moment the action becomes available.
 */
export function TypedConfirmationField({
  noun,
  name,
  value,
  onChange,
  onSubmit,
  disabled,
}: Omittable<{
  /** What the name names, lowercase: "workspace", "workflow", "team". */
  noun: string;
  name: string;
  value: string;
  onChange: (value: string) => void;
  /** Fired on Enter once the typed value matches; the footer's confirm. */
  onSubmit?: () => void;
  disabled?: boolean;
}>) {
  const matches = confirmationMatches(value, name);
  return (
    <Field>
      <FieldLabel>
        <Box direction="row" align="center" gap="xs" wrap>
          <Text size={null}>Type the {noun} name to confirm</Text>
          <Badge size="tag" tone="outline">
            {name}
          </Badge>
        </Box>
      </FieldLabel>
      <Input
        autoComplete="off"
        autoFocus
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && matches && !disabled) onSubmit?.();
        }}
        placeholder={name}
        spellCheck={false}
        value={value}
      />
    </Field>
  );
}
